import { useMemo, useState, useCallback, useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader } from 'components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Combobox } from 'components/ui/combobox';
import { createBuilding, updateBuildingStatusBulk, BuildingStatusUpdate } from 'services/api/buildings';
import { toast } from 'sonner';
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Building2, Loader2 } from 'lucide-react';
import { useHomePageData } from '../../hooks/useHomePageData';

type SortKey = 'name' | 'address' | 'organizationId' | 'createdAt';
type SortDirection = 'asc' | 'desc';

const BuildingsPage = () => {
  const { user } = useAuth();
  const { data, loading: dataLoading, error: dataError, refetch } = useHomePageData(user?.role || null);
  
  // Extract data from the cached data (memoized to stabilize deps)
  const buildings = useMemo(() => data?.buildings || [], [data]);
  const organizations = useMemo(() => data?.organizations || [], [data]);
  
  // Local state for buildings to manage updates without refetch
  const [localBuildings, setLocalBuildings] = useState(buildings);

  // State for the creation dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreatingBuilding, setIsCreatingBuilding] = useState(false);
  const [newBuildingName, setNewBuildingName] = useState('');
  const [newBuildingAddress, setNewBuildingAddress] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [search, setSearch] = useState('');
  
  // Status filter state
  const [statusFilter, setStatusFilter] = useState<'all' | 'enabled' | 'disabled'>('all');
  
  // Organization filter state
  const [organizationFilter, setOrganizationFilter] = useState<string>('');
  
  // Items per page state
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  
  // Bulk selection state
  const [selectedBuildingIds, setSelectedBuildingIds] = useState<string[]>([]);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  
  // Loading state for status updates
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  
  // Sorting state
  const [sortKey, setSortKey] = useState<SortKey>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  const getOrganizationName = useCallback((orgId: string) => {
    return organizations.find(o => o.id === orgId)?.name || orgId;
  }, [organizations]);

  // Sync localBuildings with buildings data
  useEffect(() => {
    setLocalBuildings(buildings);
  }, [buildings]);

  // Handle sorting
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  // Get sort icon
  const getSortIcon = (key: SortKey) => {
    if (sortKey !== key) return <ArrowUpDown className="h-4 w-4" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date: Date;
      
      // Handle Firestore timestamp format {_seconds, _nanoseconds}
      if (timestamp._seconds && typeof timestamp._seconds === 'number') {
        date = new Date(timestamp._seconds * 1000);
      }
      // Handle Firestore Timestamp object
      else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Handle regular Date object or timestamp number
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      }
      // Handle ISO string
      else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      }
      else {
        return 'N/A';
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatShortDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    try {
      let date: Date;
      if (timestamp._seconds && typeof timestamp._seconds === 'number') {
        date = new Date(timestamp._seconds * 1000);
      } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else {
        return 'N/A';
      }
      if (isNaN(date.getTime())) return 'N/A';
      const dd = String(date.getDate()).padStart(2, '0');
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const yyyy = date.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch {
      return 'N/A';
    }
  };

  const handleCreateBuilding = async () => {
    if (!newBuildingName.trim() || !newBuildingAddress.trim() || !selectedOrgId) {
        toast.warning("All fields are required.");
        return;
    }
    try {
      setIsCreatingBuilding(true);
      await createBuilding({ 
        name: newBuildingName, 
        address: newBuildingAddress, 
        organizationId: selectedOrgId 
      });
      toast.success("Building created successfully!");
      // Reset form and close dialog
      setNewBuildingName('');
      setNewBuildingAddress('');
      setSelectedOrgId('');
      setIsDialogOpen(false);
      // Refresh the cached data
      await refetch();
    } catch (err) {
      toast.error('Failed to create building.');
      console.error(err);
    } finally {
      setIsCreatingBuilding(false);
    }
  };

  // Bulk status update functions
  const handleBulkUpdateStatus = async (enable: boolean) => {
    if (selectedBuildingIds.length === 0) {
      toast.warning(`Please select at least one building to ${enable ? 'enable' : 'disable'}.`);
      return;
    }
    
    const payload: BuildingStatusUpdate[] = selectedBuildingIds.map(id => ({ 
      building_id: id, 
      is_enabled: enable 
    }));
    
    try {
      setIsBulkUpdating(true);
      await updateBuildingStatusBulk(payload);
      
      toast.success(`Successfully ${enable ? 'enabled' : 'disabled'} ${selectedBuildingIds.length} building(s).`);
      
      // Update local state instead of refetch
      setLocalBuildings(prevBuildings => 
        prevBuildings.map(b => 
          selectedBuildingIds.includes(b.id) ? { ...b, is_enabled: enable } : b
        )
      );
      setSelectedBuildingIds([]); // Clear selection
    } catch (err) {
      toast.error('Failed to update building status.');
      console.error(err);
    } finally {
      setIsBulkUpdating(false);
    }
  };

  // Single building status toggle using bulk API
  const handleToggleBuildingStatus = async (buildingId: string, currentStatus: boolean) => {
    try {
      setIsUpdatingStatus(true);
      const payload: BuildingStatusUpdate[] = [{ 
        building_id: buildingId, 
        is_enabled: !currentStatus 
      }];
      
      await updateBuildingStatusBulk(payload);
      toast.success(`Building ${!currentStatus ? 'enabled' : 'disabled'} successfully!`);
      
      // Update local state instead of refetch
      setLocalBuildings(prevBuildings => 
        prevBuildings.map(b => 
          b.id === buildingId ? { ...b, is_enabled: !currentStatus } : b
        )
      );
    } catch (err) {
      toast.error('Failed to update building status.');
      console.error(err);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const organizationOptions = organizations.map(org => ({
    value: org.id,
    label: org.name,
  }));

  // Items per page options
  const itemsPerPageOptions = [
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
  ];

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = localBuildings.filter(b => 
      term ? (
        (b.name || '').toLowerCase().includes(term) || 
        (b.address || '').toLowerCase().includes(term) || 
        (b.indiri || '').toLowerCase().includes(term) ||
        (b.descri || '').toLowerCase().includes(term) ||
        getOrganizationName(b.organizationId).toLowerCase().includes(term)
      ) : true
    );
    
    // Apply status filter
    if (statusFilter !== 'all') {
      const isEnabledFilter = statusFilter === 'enabled';
      list = list.filter(b => {
        // Default to true if is_enabled is undefined (for backward compatibility)
        const isEnabled = b.is_enabled !== undefined ? b.is_enabled : true;
        return isEnabled === isEnabledFilter;
      });
    }
    
    // Apply organization filter
    if (organizationFilter) {
      list = list.filter(b => b.organizationId === organizationFilter);
    }
    
    // Apply sorting
    list.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortKey) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'address':
          aValue = (a.address || '').toLowerCase();
          bValue = (b.address || '').toLowerCase();
          break;
        case 'organizationId':
          aValue = getOrganizationName(a.organizationId).toLowerCase();
          bValue = getOrganizationName(b.organizationId).toLowerCase();
          break;
        case 'createdAt':
          aValue = a.createdAt;
          bValue = b.createdAt;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return list;
  }, [localBuildings, search, statusFilter, organizationFilter, sortKey, sortDirection, getOrganizationName]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedBuildings = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Checkbox selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Only select buildings in current page
      setSelectedBuildingIds(paginatedBuildings.map(b => b.id));
    } else {
      setSelectedBuildingIds([]);
    }
  };

  const handleSelectBuilding = (buildingId: string, checked: boolean) => {
    if (checked) {
      setSelectedBuildingIds(prev => [...prev, buildingId]);
    } else {
      setSelectedBuildingIds(prev => prev.filter(id => id !== buildingId));
    }
  };

  const isAllSelected = paginatedBuildings.length > 0 && paginatedBuildings.every(b => selectedBuildingIds.includes(b.id));
  const isIndeterminate = paginatedBuildings.some(b => selectedBuildingIds.includes(b.id)) && !isAllSelected;

  if (dataLoading) return (
    <div className="space-y-6">
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
      <div className="h-96 bg-gray-200 rounded animate-pulse"></div>
    </div>
  );
  
  if (dataError) return (
    <div className="text-center text-red-600 p-4">
      <p>{dataError}</p>
      <Button onClick={refetch} className="mt-4">Retry</Button>
    </div>
  );

  return (
    <div className="space-y-4 pt-2 max-w-full overflow-hidden">
      {/* Loading Overlay */}
      {(isUpdatingStatus || isBulkUpdating) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center space-y-4 shadow-xl">
            <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
            <div className="text-center">
              <p className="text-lg font-semibold text-gray-900">
                {isBulkUpdating ? 'Updating Buildings...' : 'Updating Building Status...'}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Please wait while we process your request
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
                  <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-gradient-to-r from-teal-100 to-emerald-100 rounded-lg">
                <Building2 className="h-8 w-8 text-teal-600" />
              </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent">Lista dei {localBuildings.length} stabili amministrati</h1>
            </div>
          <p className="text-teal-400 font-medium ml-14">Esempio: Lista dei {localBuildings.length} stabili amministrati.</p>
          </div>
        {user?.role === 'admin' && (
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700">Create Building</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Create a new building</DialogTitle>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="name" className="text-right">Name</Label>
                  <Input 
                    id="name" 
                    value={newBuildingName} 
                    onChange={(e) => setNewBuildingName(e.target.value)} 
                    disabled={isCreatingBuilding}
                    className="col-span-3" 
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="address" className="text-right">Address</Label>
                  <Input 
                    id="address" 
                    value={newBuildingAddress} 
                    onChange={(e) => setNewBuildingAddress(e.target.value)} 
                    disabled={isCreatingBuilding}
                    className="col-span-3" 
                  />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                  <Label htmlFor="organization" className="text-right">Organization</Label>
                  <div className="col-span-3">
                    <Combobox
                      options={organizationOptions}
                      value={selectedOrgId}
                      onChange={setSelectedOrgId}
                      placeholder="Select an organization..."
                      searchPlaceholder="Search organizations..."
                      emptyText="No organizations found."
                      className="w-full"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setIsDialogOpen(false)}
                  disabled={isCreatingBuilding}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  onClick={handleCreateBuilding}
                  disabled={isCreatingBuilding}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {isCreatingBuilding ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Building2 className="h-4 w-4 mr-2" />
                      Create Building
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <Card className="bg-gradient-to-r from-white to-gray-50 border-gray-200">
        <CardHeader>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
              <div className="flex items-center gap-2">
                <Label htmlFor="search" className="text-sm text-teal-800 whitespace-nowrap">Cerca</Label>
                <Input
                  id="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Cerca stabile..."
                  className="w-full sm:w-64"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="organizationFilter" className="text-sm text-teal-800 whitespace-nowrap">Organization</Label>
                <select
                  id="organizationFilter"
                  value={organizationFilter}
                  onChange={(e) => setOrganizationFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent w-full sm:w-56"
                >
                  <option value="">Tutte le organizzazioni</option>
                  {organizationOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="statusFilter" className="text-sm text-teal-800 whitespace-nowrap">Stato</Label>
                <select
                  id="statusFilter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as 'all' | 'enabled' | 'disabled')}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent w-full sm:w-32"
                >
                  <option value="all">Tutti</option>
                  <option value="enabled">Abilitati</option>
                  <option value="disabled">Disabilitati</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="itemsPerPage" className="text-sm text-teal-800 whitespace-nowrap">Mostra</Label>
                <select
                  id="itemsPerPage"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing items per page
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent w-full sm:w-20"
                >
                  {itemsPerPageOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Bulk Actions */}
            {selectedBuildingIds.length > 0 && (
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
                <span className="text-sm text-teal-800 whitespace-nowrap">
                  {selectedBuildingIds.length} selezionati
                </span>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleBulkUpdateStatus(true)}
                    disabled={isBulkUpdating}
                    className="bg-green-600 hover:bg-green-700 text-white flex-1 sm:flex-none"
                    size="sm"
                  >
                    {isBulkUpdating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        <span className="hidden sm:inline">Abilitando...</span>
                        <span className="sm:hidden">...</span>
                      </>
                    ) : (
                      <>
                        <span className="hidden sm:inline">Abilita Selezionati</span>
                        <span className="sm:hidden">Abilita</span>
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={() => handleBulkUpdateStatus(false)}
                    disabled={isBulkUpdating}
                    className="bg-red-600 hover:bg-red-700 text-white flex-1 sm:flex-none"
                    size="sm"
                  >
                    {isBulkUpdating ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        <span className="hidden sm:inline">Disabilitando...</span>
                        <span className="sm:hidden">...</span>
                      </>
                    ) : (
                      <>
                        <span className="hidden sm:inline">Disabilita Selezionati</span>
                        <span className="sm:hidden">Disabilita</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto max-w-full">
            <Table className="w-full table-auto">
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-teal-100 to-emerald-100 border-teal-200">
                  <TableHead className="text-teal-900 font-semibold text-center w-12">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = isIndeterminate;
                      }}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                    />
                  </TableHead>
                  <TableHead className="text-teal-900 font-semibold text-center min-w-[200px]">
                    <Button
                      variant="ghost"
                      onClick={() => handleSort('name')}
                      className="h-auto p-0 font-semibold hover:bg-teal-200 text-teal-900"
                    >
                      Stabile {getSortIcon('name')}
                    </Button>
                  </TableHead>
                  <TableHead className="text-teal-900 font-semibold text-center w-1/5">Indirizzo</TableHead>
                  <TableHead className="text-teal-900 font-semibold text-center w-1/6">Città</TableHead>
                  <TableHead className="text-teal-900 font-semibold text-center w-1/6">Ultimo download</TableHead>
                  <TableHead className="text-teal-900 font-semibold text-center w-1/5">Periodo bonifici</TableHead>
                  <TableHead className="text-teal-900 font-semibold text-center w-1/6">
                    <Button
                      variant="ghost"
                      onClick={() => handleSort('organizationId')}
                      className="h-auto p-0 font-semibold hover:bg-teal-200 text-teal-900"
                    >
                      Organization {getSortIcon('organizationId')}
                    </Button>
                  </TableHead>
                  <TableHead className="text-teal-900 font-semibold text-center w-1/6">
                    <Button
                      variant="ghost"
                      onClick={() => handleSort('createdAt')}
                      className="h-auto p-0 font-semibold hover:bg-teal-200 text-teal-900"
                    >
                      Created Date {getSortIcon('createdAt')}
                    </Button>
                  </TableHead>
                  {user?.role === 'admin' && <TableHead className="text-teal-900 font-semibold text-center w-32">Stato</TableHead>}
                </TableRow>
              </TableHeader>
            <TableBody>
              {paginatedBuildings.length > 0 ? (
                paginatedBuildings.map((building) => {
                  const isEnabled = building.is_enabled !== undefined ? building.is_enabled : true;
                  const isSelected = selectedBuildingIds.includes(building.id);
                  
                  return (
                    <TableRow key={building.id} className="hover:bg-gray-50">
                      <TableCell className="text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleSelectBuilding(building.id, e.target.checked)}
                          className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                        />
                      </TableCell>
                      <TableCell className="text-left whitespace-normal break-words text-gray-700">
                        {`${building.name} - ${building.descri || ''}`.trim()}
                      </TableCell>
                      <TableCell className="text-center whitespace-normal break-words text-gray-700">
                        {building.indiri || building.address || 'N/A'}
                      </TableCell>
                      <TableCell className="text-center whitespace-normal break-words text-gray-700">
                        {building.citind || 'N/A'}
                      </TableCell>
                      <TableCell className="text-center whitespace-normal break-words text-gray-700">
                        {formatShortDate(building.last_versamenti_csv_download_at)}
                      </TableCell>
                      <TableCell className="text-center whitespace-normal break-words text-gray-700">
                        {`${formatShortDate(building.last_bank_file_tx_min_date)} – ${formatShortDate(building.last_bank_file_tx_max_date)}`}
                      </TableCell>
                      <TableCell className="text-center whitespace-normal break-words text-gray-700">
                        {getOrganizationName(building.organizationId)}
                      </TableCell>
                      <TableCell className="text-center text-gray-700">
                        {formatDate(building.createdAt)}
                      </TableCell>
                      {user?.role === 'admin' && (
                        <TableCell className="text-center">
                          {/* Toggle Switch */}
                          <button
                            onClick={() => handleToggleBuildingStatus(building.id, isEnabled)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-1 ${
                              isEnabled ? 'bg-teal-600' : 'bg-gray-200'
                            }`}
                            title={isEnabled ? 'Disable building' : 'Enable building'}
                          >
                            <span
                              className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                                isEnabled ? 'translate-x-5' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={user?.role === 'admin' ? 9 : 8} className="h-24 text-center">
                    No buildings found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between space-x-2 py-4">
              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Precedente
                </Button>
                
                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {(() => {
                    const maxVisiblePages = 5;
                    const pages = [];
                    
                    if (totalPages <= maxVisiblePages) {
                      // Show all pages if total is small
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(
                          <Button
                            key={i}
                            variant={currentPage === i ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(i)}
                            className="w-8 h-8 p-0"
                          >
                            {i}
                          </Button>
                        );
                      }
                    } else {
                      // Show smart pagination with ellipsis
                      const startPage = Math.max(1, currentPage - 2);
                      const endPage = Math.min(totalPages, currentPage + 2);
                      
                      // Always show first page
                      pages.push(
                        <Button
                          key={1}
                          variant={currentPage === 1 ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(1)}
                          className="w-8 h-8 p-0"
                        >
                          1
                        </Button>
                      );
                      
                      // Show ellipsis if needed
                      if (startPage > 2) {
                        pages.push(
                          <span key="ellipsis1" className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }
                      
                      // Show middle pages
                      for (let i = startPage; i <= endPage; i++) {
                        if (i !== 1 && i !== totalPages) {
                          pages.push(
                            <Button
                              key={i}
                              variant={currentPage === i ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCurrentPage(i)}
                              className="w-8 h-8 p-0"
                            >
                              {i}
                            </Button>
                          );
                        }
                      }
                      
                      // Show ellipsis if needed
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="ellipsis2" className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }
                      
                      // Always show last page
                      if (totalPages > 1) {
                        pages.push(
                          <Button
                            key={totalPages}
                            variant={currentPage === totalPages ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(totalPages)}
                            className="w-8 h-8 p-0"
                          >
                            {totalPages}
                          </Button>
                        );
                      }
                    }
                    
                    return pages;
                  })()}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Successivo
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BuildingsPage; 